/**
 * Free Gift Cart Safeguard
 * ------------------------
 * Two enforcement rules for the free book gift (product ID 8732598337726):
 *
 *  Rule 1 — Subtotal gate:
 *    If the qualifying subtotal (cart total minus the gift's own line price)
 *    drops below $65, remove the gift entirely. Mirrors the PowerX
 *    checkout-side rule so both layers stay in sync.
 *
 *  Rule 2 — Quantity gate:
 *    Only 1 unit of the free gift is ever allowed. If the total quantity
 *    across ALL gift lines exceeds 1, correct it down to exactly 1 by
 *    updating the first line to qty 1 and zeroing any duplicate lines.
 *
 * Both rules share the same lock, gate, and pub/sub pipeline so they
 * never conflict or stack concurrent requests.
 *
 * Accelerated checkout gate:
 *    While any correction is in flight, all .additional-checkout-buttons
 *    containers are locked via the `inert` attribute + a CSS overlay.
 *    This is the only method that reliably blocks pointer events, keyboard
 *    navigation, AND iframe focus simultaneously (Shop Pay, PayPal, G Pay).
 *    The gate is released only after the cartUpdate pub/sub confirms the
 *    DOM has re-rendered.
 *
 * Constraints honoured:
 *   - Zero polling / zero MutationObserver / zero reloads
 *   - Plugs into FoxTheme pub/sub + cart:grouped-sections
 *   - Single isRemoving lock prevents all concurrent requests
 *   - Gate always released on error so customer is never permanently stuck
 *   - Double-registration guard for Shopify theme-editor reloads
 */
(function () {
  'use strict';

  // ── Config ────────────────────────────────────────────────────────────────

  const FREE_GIFT_PRODUCT_ID = 8732598337726;

  /**
   * $65 mirrors the PowerX checkout-side threshold.
   * Allows legitimate discount codes on a $99 cart (~$79 after 20% off)
   * to keep the gift while still catching intentional abuse.
   */
  const THRESHOLD_CENTS = 65 * 100;

  // ── Styles ────────────────────────────────────────────────────────────────

  function injectStyles() {
    if (document.getElementById('fgs-styles')) return;
    const style = document.createElement('style');
    style.id = 'fgs-styles';
    style.textContent = [
      '.fgs-gate-wrap { position: relative; }',
      '.fgs-gate-wrap::after {',
      '  content: "";',
      '  display: block;',
      '  position: absolute;',
      '  inset: 0;',
      '  z-index: 9999;',
      '  cursor: not-allowed;',
      '  background: rgba(255, 255, 255, 0.55);',
      '  border-radius: inherit;',
      '}',
    ].join('\n');
    document.head.appendChild(style);
  }

  // ── Accelerated checkout gate ─────────────────────────────────────────────

  function getAccelContainers() {
    return Array.from(document.querySelectorAll('.additional-checkout-buttons'));
  }

  function lockAccelButtons() {
    getAccelContainers().forEach(function (el) {
      el.setAttribute('inert', '');
      el.setAttribute('aria-hidden', 'true');
      var wrapper = el.parentElement;
      if (wrapper) wrapper.classList.add('fgs-gate-wrap');
    });
  }

  function unlockAccelButtons() {
    getAccelContainers().forEach(function (el) {
      el.removeAttribute('inert');
      el.removeAttribute('aria-hidden');
      var wrapper = el.parentElement;
      if (wrapper) wrapper.classList.remove('fgs-gate-wrap');
    });
  }

  // ── Core logic ────────────────────────────────────────────────────────────

  function whenReady(fn) {
    if (typeof FoxTheme !== 'undefined' && FoxTheme.pubsub) {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn, { once: true });
    }
  }

  whenReady(function () {
    if (window.__freeGiftSafeguardBound) return;
    window.__freeGiftSafeguardBound = true;

    injectStyles();

    /** Single lock for both rules — prevents any stacked concurrent requests */
    var isUpdating = false;

    /**
     * Fetch fresh cart state if no payload provided, then run both rules.
     * Returns early at the first rule that needs action (they are mutually
     * exclusive: if qty > 1, fix qty first; subtotal re-evaluated next tick).
     *
     * @param {Object|null} cart  Shopify cart JSON or null to fetch fresh
     */
    function evaluate(cart) {
      var fetchCart = cart
        ? Promise.resolve(cart)
        : fetch(FoxTheme.routes.cart_url + '.js', FoxTheme.utils.fetchConfig())
            .then(function (res) { return res.ok ? res.json() : null; });

      fetchCart
        .then(function (cartData) {
          if (!cartData) return;

          var items = Array.isArray(cartData.items) ? cartData.items : [];
          if (items.length === 0) return;

          // Collect every line that belongs to the free gift product
          var giftLines = items.filter(function (item) {
            return Number(item.product_id) === FREE_GIFT_PRODUCT_ID;
          });
          if (giftLines.length === 0) return;

          // ── Rule 2: Quantity gate ───────────────────────────────────────
          // Sum quantities across ALL gift lines (covers both qty > 1 on a
          // single line and the same variant spread across multiple lines).
          var totalGiftQty = giftLines.reduce(function (sum, line) {
            return sum + line.quantity;
          }, 0);

          if (totalGiftQty > 1) {
            enforceQuantityOne(giftLines);
            // Return here: once qty is corrected the pub/sub will re-fire
            // evaluate() and Rule 1 will run cleanly on the corrected cart.
            return;
          }

          // ── Rule 1: Subtotal gate ───────────────────────────────────────
          var giftLineTotalCents = giftLines.reduce(function (sum, line) {
            return sum + line.final_line_price;
          }, 0);
          var qualifyingSubtotal =
            Number(cartData.items_subtotal_price || 0) - giftLineTotalCents;

          if (qualifyingSubtotal >= THRESHOLD_CENTS) return;

          removeGiftLines(giftLines);
        })
        .catch(function () { /* silent */ });
    }

    /**
     * Rule 2 correction: set the first gift line to qty 1 and zero out
     * any additional lines. Uses a single cart/update.js call with an
     * updates object keyed by variant ID.
     *
     * Edge case handled: if the same variant_id appears on two lines
     * (can happen with selling plan vs. OTP splits), we zero the variant
     * entirely and then add back 1 — but in practice the gift has no
     * selling plan, so a simple "first line = 1, rest = 0" is sufficient.
     *
     * @param {Array} giftLines  All cart lines for the free gift product
     */
    function enforceQuantityOne(giftLines) {
      if (isUpdating) return;
      isUpdating = true;
      lockAccelButtons();

      try {
        var sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        var config = FoxTheme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        var formData = new FormData();

        // Keep exactly 1 on the first line; zero everything else.
        // If multiple lines share the same variant_id the last append wins,
        // which would be 0 — so we process the keeper last.
        var keepLine = giftLines[0];
        var extraLines = giftLines.slice(1);

        extraLines.forEach(function (line) {
          formData.append('updates[' + line.variant_id + ']', '0');
        });

        // Set the keeper last so its value wins if variant_ids collide
        formData.append('updates[' + keepLine.variant_id + ']', '1');

        if (sectionsToBundle.length > 0) {
          formData.append('sections', sectionsToBundle.join(','));
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        fetch(FoxTheme.routes.cart_update_url, config)
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (updatedCart) {
            if (!updatedCart) return;
            FoxTheme.pubsub.publish(
              FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
              { cart: updatedCart }
            );
            // Gate released inside the pub/sub subscriber after DOM update
          })
          .catch(function () { unlockAccelButtons(); })
          .finally(function () { isUpdating = false; });

      } catch (err) {
        unlockAccelButtons();
        isUpdating = false;
      }
    }

    /**
     * Rule 1 correction: remove all gift lines entirely.
     *
     * @param {Array} giftLines  All cart lines for the free gift product
     */
    function removeGiftLines(giftLines) {
      if (isUpdating) return;
      isUpdating = true;
      lockAccelButtons();

      try {
        var sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', {
            bubbles: true,
            detail: { sections: sectionsToBundle },
          })
        );

        var config = FoxTheme.utils.fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        var formData = new FormData();
        giftLines.forEach(function (line) {
          formData.append('updates[' + line.variant_id + ']', '0');
        });
        if (sectionsToBundle.length > 0) {
          formData.append('sections', sectionsToBundle.join(','));
          formData.append('sections_url', window.location.pathname);
        }
        config.body = formData;

        fetch(FoxTheme.routes.cart_update_url, config)
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (updatedCart) {
            if (!updatedCart) return;
            FoxTheme.pubsub.publish(
              FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
              { cart: updatedCart }
            );
          })
          .catch(function () { unlockAccelButtons(); })
          .finally(function () { isUpdating = false; });

      } catch (err) {
        unlockAccelButtons();
        isUpdating = false;
      }
    }

    // ── Bindings ─────────────────────────────────────────────────────────────

    // 1. Run on load — catches direct /cart navigation with a pre-existing
    //    invalid state (qty > 1 or subtotal already < $65)
    evaluate(null);

    // 2. Run on every cart update — covers quantity changes, item removals,
    //    discount applications, and the re-evaluation after our own corrections
    FoxTheme.pubsub.subscribe(
      FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate,
      function (payload) {
        if (payload && payload.cart && !payload.cart.errors) {
          // Release gate first — evaluate() will immediately re-lock if
          // another correction is still needed, otherwise stays unlocked
          unlockAccelButtons();
          evaluate(payload.cart);
        }
      }
    );
  });

})();